/**
 * Cache Configuration
 * Centralized config for all caching behavior across the app
 */

export interface CacheConfig {
  /** Time-to-live in milliseconds */
  ttlMs: number;
  /** Key used for localStorage */
  storageKey: string;
}

export const cacheConfig = {
  /** Favorites list cache - 5 minutes TTL */
  favorites: {
    ttlMs: 5 * 60 * 1000, // 5 minutes
    storageKey: 'drug_favorites_cache',
  } as CacheConfig,

  /** Catalog listings cache - 5 minutes TTL */
  catalog: {
    ttlMs: 5 * 60 * 1000, // 5 minutes
    storageKey: 'drug_catalog_cache',
  } as CacheConfig,

  /** Chat rooms list cache - 1 minute TTL */
  chatRooms: {
    ttlMs: 1 * 60 * 1000, // 1 minute
    storageKey: 'drug_chat_rooms_cache',
  } as CacheConfig,

  /** Pet detail cache - 5 minutes TTL */
  petDetail: {
    ttlMs: 5 * 60 * 1000, // 5 minutes
    storageKey: 'drug_pet_detail_cache',
  } as CacheConfig,

  /** For You page cache - 5 minutes TTL */
  forYou: {
    ttlMs: 5 * 60 * 1000, // 5 minutes
    storageKey: 'drug_for_you_cache',
  } as CacheConfig,

  /** User profile cache - 5 minutes TTL */
  userProfile: {
    ttlMs: 5 * 60 * 1000, // 5 minutes
    storageKey: 'drug_user_profile_cache',
  } as CacheConfig,

  /** Viewer data cache - 5 minutes TTL */
  viewer: {
    ttlMs: 5 * 60 * 1000, // 5 minutes
    storageKey: 'drug_viewer_cache',
  } as CacheConfig,

  /** Quiz profile cache - 10 minutes TTL */
  quizProfile: {
    ttlMs: 10 * 60 * 1000, // 10 minutes
    storageKey: 'drug_quiz_profile_cache',
  } as CacheConfig,

  /** AI filter profile cache - 10 minutes TTL */
  aiFilterProfile: {
    ttlMs: 10 * 60 * 1000, // 10 minutes
    storageKey: 'drug_ai_filter_profile_cache',
  } as CacheConfig,

  /** Species counts cache - 2 minutes TTL */
  speciesCounts: {
    ttlMs: 2 * 60 * 1000, // 2 minutes
    storageKey: 'drug_species_counts_cache',
  } as CacheConfig,
};

/**
 * Generic cache entry structure
 */
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Check if cache entry is still valid
 */
export function isCacheValid<T>(entry: CacheEntry<T> | null, ttlMs: number): boolean {
  if (!entry) return false;
  return Date.now() - entry.timestamp < ttlMs;
}

/**
 * Get cached data if valid
 */
export function getCachedData<T>(storageKey: string, ttlMs: number): T | null {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    
    const entry: CacheEntry<T> = JSON.parse(stored);
    if (isCacheValid(entry, ttlMs)) {
      return entry.data;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(storageKey);
    return null;
  } catch {
    return null;
  }
}

/**
 * Set cache data
 */
export function setCacheData<T>(storageKey: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(storageKey, JSON.stringify(entry));
  } catch {
    // Storage might be full, ignore
  }
}

/**
 * Invalidate cache
 */
export function invalidateCache(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Ignore
  }
}

/**
 * Clear all known application caches without affecting user session
 */
export function clearAllCaches(): void {
  try {
    // Clear all cache config keys
    Object.values(cacheConfig).forEach(config => {
      localStorage.removeItem(config.storageKey);
    });
    // Clear additional known cache keys
    localStorage.removeItem('drug_feature_flags');
    localStorage.removeItem('drug_quiz_config');
  } catch {
    // Ignore
  }
}

export default cacheConfig;
